import React, { useState, useEffect, useRef } from 'react';
import { notesApi } from '../../api';
import { Icon } from '../common/Icons';

function timeAgo(iso) {
  if (!iso) return 'recently';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function readingTime(text) {
  if (!text) return '< 1 min read';
  const words = text.trim().split(/\s+/).length;
  const mins = Math.ceil(words / 180);
  return `${mins} min read`;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ACCESS_LABEL = {
  VIEW: 'Viewed in Reader',
  SEARCH_HIT: 'Discovered in Search',
  UPDATE: 'Edited & Updated'
};

export default function NoteDetailModal({ note, doc, onClose, onEdit, onDelete, onNoteUpdated, onDocUpdated }) {
  const [currentNote, setCurrentNote] = useState(note || doc);
  const [history, setHistory] = useState([]);
  const [loadingH, setLoadingH] = useState(false);
  const [tab, setTab] = useState('content'); // 'content' | 'history'
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const menuRef = useRef(null);
  const recordedNoteIdRef = useRef(null);

  const notifyUpdated = (updated) => {
    onNoteUpdated?.(updated);
    onDocUpdated?.(updated);
  };

  // Close hamburger menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Immediately fetch fresh note to record access in backend & increment view count (deduplicated against React StrictMode)
  useEffect(() => {
    const item = note || doc;
    setCurrentNote(item);
    if (item?.id && recordedNoteIdRef.current !== item.id) {
      recordedNoteIdRef.current = item.id;
      notesApi.getById(item.id)
        .then(r => {
          if (r?.data) {
            setCurrentNote(r.data);
            notifyUpdated(r.data);
          }
        })
        .catch(() => {});
    }
  }, [note?.id, doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'history' && currentNote?.id) {
      setLoadingH(true);
      notesApi.history(currentNote.id)
        .then(r => setHistory(r.data))
        .catch(() => {})
        .finally(() => setLoadingH(false));
    }
  }, [tab, currentNote?.id]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!currentNote) return null;

  const handleCopy = () => {
    const textToCopy = `${currentNote?.title ? `${currentNote.title}\n\n` : ''}${currentNote?.content || ''}`.trim();
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleToggleStar = async () => {
    if (!currentNote?.id) return;
    try {
      const { data: updated } = await notesApi.toggleStar(currentNote.id);
      setCurrentNote(updated);
      notifyUpdated(updated);
    } catch {}
  };

  const wordCount = currentNote.content ? currentNote.content.trim().split(/\s+/).length : 0;
  const categoryName = currentNote.categoryName || currentNote.folderName || 'General';
  const isStarred = !!(currentNote.starred ?? currentNote.favorite);

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet-box detail-modal-box" role="dialog" aria-modal="true">
        <div className="sheet-drag-handle" />

        <div className="modal-header">
          <div className="detail-header-main">
            <h2 className="modal-title">{currentNote.title}</h2>
            <div className="detail-meta-row">
              <span className="detail-meta-pill category-pill">
                <Icon name="category" size={13} />
                {categoryName}
              </span>
              <span className="detail-meta-pill">
                <Icon name="eye" size={13} />
                {currentNote.hitCount ?? 0} views
              </span>
              <span className="detail-meta-pill">
                <Icon name="clock" size={13} />
                {readingTime(currentNote.content)}
              </span>
              <span className="detail-meta-pill">
                <Icon name="history" size={13} />
                {timeAgo(currentNote.lastAccessedAt)}
              </span>
              <span className="detail-meta-pill">
                {wordCount} words
              </span>
            </div>
          </div>

          <div className="detail-header-actions">
            {/* Star button on top right when opened */}
            <button
              type="button"
              className={`btn-star ${isStarred ? 'active' : ''}`}
              onClick={handleToggleStar}
              title={isStarred ? 'Unstar Note' : 'Star Note'}
              aria-label={isStarred ? 'Unstar note' : 'Star note'}
            >
              <Icon name="star" size={18} filled={isStarred} />
            </button>

            {/* Hamburger menu for copy, edit, delete */}
            <div className="card-hamburger-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className={`action-btn-pill menu-trigger ${menuOpen ? 'active' : ''}`}
                title="More actions"
                onClick={() => setMenuOpen(prev => !prev)}
                aria-label="More actions"
              >
                <Icon name="more-vertical" size={16} />
              </button>

              {menuOpen && (
                <div className="card-menu-dropdown right-aligned">
                  <button
                    type="button"
                    className="card-menu-item"
                    onClick={() => {
                      handleCopy();
                      setMenuOpen(false);
                    }}
                  >
                    <Icon name={copied ? 'check' : 'copy'} size={14} />
                    <span>{copied ? 'Copied!' : 'Copy Content'}</span>
                  </button>
                  <button
                    type="button"
                    className="card-menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit?.(currentNote);
                    }}
                  >
                    <Icon name="edit" size={14} />
                    <span>Edit Note</span>
                  </button>
                  {onDelete && (
                    <button
                      type="button"
                      className="card-menu-item danger"
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete?.(currentNote);
                      }}
                    >
                      <Icon name="trash" size={14} />
                      <span>Move to Trash</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="btn-icon"
              onClick={onClose}
              aria-label="Close modal"
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="detail-tab-nav">
          <button
            type="button"
            className={`detail-tab-btn ${tab === 'content' ? 'active' : ''}`}
            onClick={() => setTab('content')}
          >
            <Icon name="note" size={15} />
            <span>Note Content</span>
          </button>
          <button
            type="button"
            className={`detail-tab-btn ${tab === 'history' ? 'active' : ''}`}
            onClick={() => setTab('history')}
          >
            <Icon name="history" size={15} />
            <span>Audit History ({currentNote.hitCount ?? 0})</span>
          </button>
        </div>

        {/* Tab Panels */}
        <div className="modal-body detail-modal-body">
          {tab === 'content' ? (
            <div className="note-content-view document-content-view">
              {currentNote.tags?.length > 0 && (
                <div className={`detail-tags-section ${tagsExpanded ? 'expanded' : 'collapsed'}`}>
                  <div className="detail-tags-list">
                    {!tagsExpanded ? (
                      <>
                        {currentNote.tags.slice(0, 3).map(t => (
                          <span key={t} className="tag-badge">#{t.startsWith('#') ? t.slice(1) : t}</span>
                        ))}
                        {currentNote.tags.length > 3 && (
                          <span className="tag-badge more" title={currentNote.tags.slice(3).map(t => `#${t}`).join(', ')}>
                            +{currentNote.tags.length - 3}
                          </span>
                        )}
                      </>
                    ) : (
                      currentNote.tags.map(t => (
                        <span key={t} className="tag-badge">#{t.startsWith('#') ? t.slice(1) : t}</span>
                      ))
                    )}
                  </div>
                  {currentNote.tags.length > 3 && (
                    <button
                      type="button"
                      className="btn-tags-toggle"
                      onClick={() => setTagsExpanded(prev => !prev)}
                      title={tagsExpanded ? 'Collapse hashtags' : 'Expand all hashtags'}
                      aria-label={tagsExpanded ? 'Collapse hashtags' : 'Expand all hashtags'}
                    >
                      <Icon name={tagsExpanded ? 'chevron-up' : 'chevron-down'} size={13} />
                    </button>
                  )}
                </div>
              )}
              <div className="note-prose-text document-prose-text">
                {currentNote.content}
              </div>
            </div>
          ) : (
            <div className="note-history-view document-history-view">
              {loadingH ? (
                <div className="loading-state-wrap">
                  <div className="spinner sm" />
                  <p className="loading-text">Loading access records...</p>
                </div>
              ) : history.length === 0 ? (
                <div className="empty-state-card sm">
                  <p className="empty-state-desc">No access events recorded yet for this note.</p>
                </div>
              ) : (
                <div className="timeline-container">
                  {history.map((h, i) => (
                    <div key={h.id || i} className="timeline-item">
                      <div className={`timeline-indicator ${h.accessType || 'VIEW'}`}>
                        <div className="timeline-dot" />
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-type-label">
                            {ACCESS_LABEL[h.accessType] || h.accessType}
                          </span>
                          <span className="timeline-time">
                            {timeAgo(h.accessedAt)}
                          </span>
                        </div>
                        <div className="timeline-timestamp">
                          {formatDate(h.accessedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
